// "Describe your use case" modal flow — formerly a direct-in-browser
// Anthropic call. Per the May 2026 functional/content review, the
// Claude/API-key surface area was removed: the modal now captures the
// visitor's use case description + email and submits it as a lead via
// the existing pilot lead pipeline. The simulation comes back via the
// founder team, not a self-serve generation step.
//
// `openCustomModal` keeps the same exported signature so existing
// callsites (Story scene 2 "Describe your own", Sandbox FAB) don't need
// to change. The `processes` / `onSelect` / `onGenerated` options are
// accepted but ignored — they were only meaningful when this modal
// surfaced the catalog + a Claude-generated process for the React tree
// to consume immediately. The new flow doesn't generate anything in the
// browser; the team follows up over email.

import { getIntake } from "./intake-bridge";
import { isValidEmail, setCapturedEmail } from "./email";
import { submitLead } from "./lead-api";
import type { ProcessDef, ProcessCatalog } from "./processes";

// Backwards-compatible API surface — kept so existing imports don't
// break. Generation is no longer self-serve in the browser; if a
// future server-side endpoint reinstates it, this would be the place
// to wire it back in.
export function getApiKey(): string {
  return "";
}
export function setApiKey(_key: string | null | undefined): void {
  /* no-op */
}
export async function generateProcess(_useCase: string, _apiKey: string): Promise<ProcessDef> {
  throw new Error("Self-serve process generation has been retired. Use the lead-capture flow.");
}

// ---------- Modal flow ----------

export interface OpenCustomModalOpts {
  processes?: ProcessCatalog;
  onSelect?: (p: ProcessDef) => void;
  onGenerated?: (p: ProcessDef) => void;
}

export type OpenCustomModalArg = OpenCustomModalOpts | ((p: ProcessDef) => void);

export function openCustomModal(_arg: OpenCustomModalArg = {}): void {
  const backdrop = document.querySelector<HTMLElement>("#modal-backdrop");
  const useCaseInput = document.querySelector<HTMLTextAreaElement>("#use-case-input");
  const emailInput = document.querySelector<HTMLInputElement>("#use-case-email");
  const errEl = document.querySelector<HTMLElement>("#modal-error");
  const sendBtn = document.querySelector<HTMLButtonElement>("#generate-btn");
  const closeBtn = document.querySelector<HTMLButtonElement>("#modal-close");
  const body = document.querySelector<HTMLElement>("#modal-body");
  const success = document.querySelector<HTMLElement>("#modal-body-success");
  const foot = document.querySelector<HTMLElement>("#modal-foot");

  if (!backdrop || !useCaseInput || !emailInput || !errEl || !sendBtn || !closeBtn) {
    return;
  }

  useCaseInput.value = "";
  // Intentionally do NOT pre-fill from the stored captured email — the
  // visitor reads a pre-filled value as their already-typed input, but
  // it's actually stale state from prior sessions / test probes. Let
  // the HTML placeholder (`you@company.com`) render as ghost text so
  // the affordance reads as "type your email here".
  emailInput.value = "";
  errEl.textContent = "";
  if (body) body.hidden = false;
  if (success) success.hidden = true;
  if (foot) foot.hidden = false;
  sendBtn.disabled = false;
  sendBtn.textContent = "Send my use case";

  const close = (): void => backdrop.classList.remove("open");
  backdrop.classList.add("open");
  setTimeout(() => useCaseInput.focus(), 50);

  closeBtn.onclick = close;
  backdrop.onclick = (e) => { if (e.target === backdrop) close(); };

  const submit = async () => {
    const useCase = useCaseInput.value.trim();
    const email = emailInput.value.trim();
    if (!useCase) { errEl.textContent = "Describe the use case you'd like us to simulate."; return; }
    if (!isValidEmail(email)) { errEl.textContent = "We'll need a valid work email to send the simulation."; return; }
    errEl.textContent = "";
    sendBtn.disabled = true;
    sendBtn.textContent = "Sending…";

    setCapturedEmail(email);

    const result = await submitLead("TALK_TO_US", {
      email,
      goals: useCase,
      context: {
        intake: getIntake(),
        process: "custom-use-case",
      },
    });

    if (!result.ok) {
      errEl.textContent = "Couldn't reach our team. Try again in a moment, or email hello@planapus.com.";
      sendBtn.disabled = false;
      sendBtn.textContent = "Send my use case";
      return;
    }

    if (body) body.hidden = true;
    if (foot) foot.hidden = true;
    if (success) success.hidden = false;
    const successText = document.querySelector("#modal-success-text");
    if (successText) {
      successText.textContent = `We'll send a tailored simulation to ${email} within one business day.`;
    }
    setTimeout(close, 2400);
  };

  sendBtn.onclick = submit;
  useCaseInput.onkeydown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
  };
  emailInput.onkeydown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); submit(); }
  };
}
